import Pipeline from 'types/pipeline';
import { PipelineTemplateSummary } from 'types/pipeline_template';

import { useAppRedirect } from 'hooks/useAppRedirect';
import { useCreatePipelineFromTemplate } from 'pipelines/hooks/useCreatePipelineFromTemplate';
import { useHasAccess } from 'hooks/useHasAccess';

import { NavRoute, DeployRoute } from 'application/types/routes';

import { Button } from 'components/Button/Button';
import { Heading } from 'components/Heading/Heading';
import { PricingPlanLabel } from 'pipelines/components/PricingPlanLabel/PricingPlanLabel';
import { Tooltip } from 'components/Tooltip/Tooltip';

export type PipelineTemplateSmallCardProps = {
  template: PipelineTemplateSummary;
};

export function PipelineTemplateSmallCard({
  template,
}: PipelineTemplateSmallCardProps) {
  const redirect = useAppRedirect();
  const [hasAccess] = useHasAccess();
  const isIncludedInPlan = template.is_included_in_plan !== false;

  const {
    error: deployError,
    isLoading: isDeploying,
    mutate: createPipelineForDeploy,
  } = useCreatePipelineFromTemplate({
    onSuccess(pipeline: Pipeline) {
      sessionStorage.setItem('rapidDeployPreselectedPipelineId', pipeline.id);
      redirect({
        pathname: `/${NavRoute.DEPLOY}/${DeployRoute.RAPID_DEPLOY}`,
      });
    },
  });

  function handleDeploy() {
    if (!isIncludedInPlan) {
      return;
    }

    createPipelineForDeploy({ template });
  }

  return (
    <div className="flex gap-2 rounded-lg border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] p-3">
      <div className="relative w-[90px] shrink-0 self-stretch">
        {template.gallery_img_url ? (
          <img
            className="absolute inset-0 my-0! h-full! w-full rounded-md bg-[hsl(var(--ui-control-bg))] object-cover"
            src={template.gallery_img_url}
            alt={template.name}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-[hsl(var(--ui-control-bg))] text-sm text-[hsl(var(--ui-text-lowContrast))]">
            Preview unavailable
          </div>
        )}

        {isDeploying && (
          <div className="loading-spinner-wrap absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
            <span className="loading-spinner" />
          </div>
        )}

        {!isIncludedInPlan && (
          <PricingPlanLabel placement="top-right" hasLink />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Heading level="4" className="m-0! [--heading-mb:0!]">
          {template.name}
        </Heading>

        {template.description && (
          <Tooltip side="right" content={template.description}>
            <span className="m-0 line-clamp-2 text-sm text-[hsl(var(--ui-text-lowContrast))]">
              {template.description}
            </span>
          </Tooltip>
        )}

        {deployError && (
          <p className="m-0 text-sm text-[hsl(var(--danger-11))]">
            {deployError.message}
          </p>
        )}

        {hasAccess('deploy_edit') && (
          <Button
            variant="secondary"
            size="small"
            onClick={handleDeploy}
            disabled={isDeploying || !isIncludedInPlan}
            aria-label={`Deploy ${template.name}`}
          >
            Deploy
          </Button>
        )}
      </div>
    </div>
  );
}
